const uniq = (value, index, self) => self.indexOf(value) === index;

export const edgesToNodes = (resource, type) => {
  if (!resource || !type) return [];
  if (!resource.edges && !resource.nodes) return resource;

  return resource.edges.map(({ node }) => {
    switch (type) {
      case 'image':
        return formatImage(node);
      case 'media':
        return formatMedia(node);
      case 'product':
        return formatProduct(node);
      case 'metafield':
        return formatMetafield(node);
      case 'variant':
        return formatVariant(node);
      default:
        console.error(
          'utils/storefront/format-resource.js did not properly parse edgesToNodes.',
        );
        return null;
    }
  });
};

export const mergeProduct = (products) => {
  const firstProduct = products.pop();
  return products.reduce((a, current) => {
    return {
      ...current,
      variants: current.variants.concat(a.variants),
    };
  }, firstProduct);
};

export const mergeProducts = (products) => {
  const groupedProducts = products.reduce(
    (a, current) => {
      const group = current.tags.find((tag) => tag.startsWith('group_id::'));
      if (group) {
        a[group] = a[group] ?? [];
        a[group].push(current);
      } else {
        a.ungrouped.push(current);
      }
      return a;
    },
    { ungrouped: [] },
  );

  const tempGroupedProducts = structuredClone(groupedProducts);
  const sortedProducts = Object.entries(tempGroupedProducts).reduce(
    (a, [key, value]) => {
      if (key === 'ungrouped') {
        a.push(...value);
      } else {
        a.push(mergeProduct(value));
      }
      return a;
    },
    [],
  );
  return { groupedProducts, sortedProducts };
};

export const formatBundle = (products) => {
  const initialBundleProducts = edgesToNodes(products, 'product');
  const { groupedProducts, sortedProducts } = mergeProducts(
    initialBundleProducts,
  );
  const bundleValue = sortedProducts.reduce(
    (total, product) =>
      (total += parseFloat(product.priceRange.minVariantPrice.amount)),
    0,
  );

  return {
    initialBundleProducts,
    products: sortedProducts,
    groupedProducts,
    value: {
      amount: bundleValue,
      currencyCode: window.Shopify.currency.active,
    },
  };
};

export const formatCartLine = (item, additionalData) => {
  return {
    ...item,
    merchandise: additionalData,
  };
};

export const formatCart = (cart, additionalItemData) => {
  return {
    ...cart,
    items: formatCartLines(cart.items, additionalItemData),
  };
};

export const formatImage = (image) => ({
  ...image,
});

export const formatMetafield = (metafield) => ({
  ...metafield,
  heading: metafield.fields.find((f) => f.key === 'heading')?.value,
});

export const formatMedia = (media) => ({
  ...(media.previewImage ? media.previewImage : media.image),
  embedUrl: media?.embedUrl,
  sources: (media?.sources && media.sources[0] && [media.sources[0]]) || null
});

export const formatProduct = (product) => ({
  ...product,
  variants: edgesToNodes(product.variants, 'variant'),
  images: edgesToNodes(product.images, 'image'),
  media: edgesToNodes(product.media, 'media'),
  hasOnlyDefaultVariant:
    product.options.length === 1 &&
    product.options[0].values[0] === 'Default Title',
});

export const formatVariant = (variant) => ({
  ...variant,
});

export const formatCartLines = (items, additionalItemData = []) => {
  if (!items) return [];

  const additionalData = additionalItemData.reduce((a, item) => {
    const id = item.id.includes('gid://shopify/ProductVariant/')
      ? item.id.split('gid://shopify/ProductVariant/')[1]
      : item.id;
    a[id] = { ...item, id };
    return a;
  }, {});

  const processedBundles = [];
  const formattedLines = items
    .map((item) => {
      const bundleProductsId = item.properties?.['_bundleProductsId'];
      const formattedCartLine = formatCartLine(item, additionalData[item.id]);
      // If not a bundle item return the default line object
      if (!bundleProductsId) return formattedCartLine;
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId)) return;

      // Get bundle items
      const bundleItems = items
        .filter((i) => {
          const currentId = i.properties?.['_bundleProductsId'];
          if (currentId === bundleProductsId) return i;
        })
        .map((i) => i)
        .map((i) => formatCartLine(i, additionalData[i.id]));

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(firstItem.properties?.['_bundleData']);
      const { bundlePrice, bundleCompareAtPrice } = bundleItems.reduce(
        (prices, item) => ({
          bundlePrice: (prices.bundlePrice +=
            parseFloat(item.final_price) / 100),
          bundleCompareAtPrice: (prices.bundleCompareAtPrice += parseFloat(
            item.merchandise.price.amount,
          )),
        }),
        { bundlePrice: 0, bundleCompareAtPrice: 0 },
      );
      const merchandise = {
        ...bundleData.merchandise,
        price: {
          amount: bundlePrice,
          currencyCode: window.Shopify?.currency?.active,
        },
        compareAtPrice: {
          amount: bundleCompareAtPrice,
          currencyCode: window.Shopify?.currency?.active,
        },
      };

      // Return bundle line item
      return {
        merchandise,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines;
};
